import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowRight, 
  Star, 
  Brain, 
  Users, 
  Target,
  Zap,
  Globe,
  Shield,
  Heart,
  Search,
  CheckCircle,
  Clock,
  Sparkles,
  Building,
  BarChart,
  MessageSquare,
  Award
} from 'lucide-react';

// Hero Section
const HeroSection = () => (
  <section className="relative overflow-hidden bg-background border-b">
    <div className="absolute inset-0 bg-grid-slate-100 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] dark:bg-grid-slate-700/25 dark:[mask-image:linear-gradient(0deg,rgba(255,255,255,0.1),rgba(255,255,255,0))]" style={{ backgroundSize: '30px 30px' }} />
    <div className="relative container px-4 md:px-6 flex flex-col md:flex-row items-center justify-between min-h-[calc(100vh-4rem)] gap-8 py-12">
      <div className="flex-1 text-center md:text-left max-w-2xl">
        <Badge variant="outline" className="mb-4">For Professionals</Badge>
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter mb-4">
          Accelerate Your{' '}
          <span className="text-primary">Professional Growth</span>
        </h1>
        <p className="text-xl text-muted-foreground mb-8">
          Build your professional brand, showcase your expertise, and connect with opportunities that align with your career goals.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
          <Button asChild size="lg" className="text-base">
            <Link to="/register">
              Get Started Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="text-base">
            <Link to="/pricing">
              View Pricing
              <Star className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </div>
      <div className="flex-1 relative max-w-xl">
        <img 
          src="https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=800&q=80" 
          alt="Professional growth"
          className="rounded-lg shadow-2xl"
        />
      </div>
    </div>
  </section>
);

// Features Section
const FeaturesSection = () => (
  <section className="py-20 px-4 md:px-6">
    <div className="container">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl">
          Everything You Need to Succeed
        </h2>
        <p className="mt-4 text-muted-foreground md:text-lg">
          Powerful tools and features designed to help you reach your career goals
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Feature cards */}
        <FeatureCard 
          icon={Brain}
          title="AI Career Planning"
          description="Get personalized career guidance powered by AI"
          features={[
            "Career path recommendations",
            "Skill gap analysis",
            "Growth opportunities"
          ]}
        />
        
        <FeatureCard 
          icon={Target}
          title="Smart Job Matching"
          description="Find opportunities that align with your skills and goals"
          features={[
            "AI-powered matching",
            "Personalized recommendations", 
            "Salary insights"
          ]}
        />

        <FeatureCard
          icon={Users}
          title="Network Building"
          description="Connect with industry professionals and grow your network"
          features={[
            "Professional networking",
            "Industry insights",
            "Mentorship opportunities"
          ]}
        />

        <FeatureCard
          icon={BarChart}
          title="Career Analytics"
          description="Track your professional growth with detailed analytics"
          features={[
            "Profile performance metrics",
            "Skills gap analysis",
            "Career progression tracking"
          ]}
        />

        <FeatureCard
          icon={MessageSquare}
          title="Interview Prep"
          description="Prepare for interviews with AI-powered tools"
          features={[
            "Practice interviews",
            "Feedback analysis",
            "Interview scheduling"
          ]}
        />

        <FeatureCard
          icon={Award}
          title="Skill Verification"
          description="Get your skills validated by industry experts"
          features={[
            "Expert validation",
            "Digital badges",
            "Skill endorsements"
          ]}
        />
      </div>
    </div>
  </section>
);

// Feature Card Component
const FeatureCard = ({ 
  icon: Icon, 
  title, 
  description, 
  features 
}: { 
  icon: any;
  title: string;
  description: string;
  features: string[];
}) => (
  <Card>
    <CardContent className="p-6">
      <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
        <Icon className="h-6 w-6 text-primary" />
      </div>
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-muted-foreground mb-4">{description}</p>
      <ul className="space-y-2">
        {features.map((feature, index) => (
          <li key={index} className="flex items-center gap-2 text-sm">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);

// Growth Section
const GrowthSection = () => (
  <section className="py-20 px-4 md:px-6 bg-muted/50">
    <div className="container">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl">
          Your Professional Growth Journey
        </h2>
        <p className="mt-4 text-muted-foreground md:text-lg">
          Track your progress and achieve your career goals
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <Card>
          <CardContent className="p-6">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
              <Target className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Set Goals</h3>
            <p className="text-muted-foreground">
              Define your career objectives and create actionable plans to achieve them
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
              <Brain className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Learn & Develop</h3>
            <p className="text-muted-foreground">
              Access resources and opportunities to build your skills
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
              <Star className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Achieve Success</h3>
            <p className="text-muted-foreground">
              Reach your career milestones and celebrate your achievements
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section className="py-20 px-4 md:px-6 bg-primary text-primary-foreground">
    <div className="container">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl">
          Ready to Take Your Career to the Next Level?
        </h2>
        <p className="mt-4 text-primary-foreground/80 md:text-xl">
          Join thousands of professionals using NewLoop to advance their careers
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            size="lg" 
            variant="secondary" 
            className="text-base"
            asChild
          >
            <Link to="/register">
              Get Started Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
          <Button 
            size="lg" 
            variant="outline" 
            className="bg-transparent border-white hover:bg-white/10 text-base"
            asChild
          >
            <Link to="/pricing">
              View Pricing
            </Link>
          </Button>
        </div>
      </div>
    </div>
  </section>
);

// Main Marketing Page Component
export default function ProfessionalsMarketingPage() {
  return (
    <div className="flex flex-col w-full">
      <HeroSection />
      <FeaturesSection />
      <GrowthSection />
      <CTASection />
    </div>
  );
}